import React, { useState } from 'react';

const SuperAdminPackageForm = ({
  packageForm,
  setPackageForm,
  isSavingPackage,
  onCancel,
  onSave,
}) => {
  const [errors, setErrors] = useState({});
  const packageFormTitle = packageForm.id ? 'Update package plan' : 'Create package plan';
  const totalSeats = Number(packageForm.max_admins || 0) + Number(packageForm.max_workers || 0);

  const validatePrice = (value) => {
    // Check if value contains decimal point
    if (value && value.toString().includes('.')) {
      return 'Decimal values are not allowed. Please enter a whole number (e.g., 25000, not 25000.50)';
    }
    
    // Check if it's a valid number
    const numValue = Number(value);
    if (value && (isNaN(numValue) || numValue <= 0)) {
      return 'Price must be a positive whole number';
    }
    
    return null;
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string or validate numbers
    if (value === '') {
      setPackageForm((prev) => ({ ...prev, price: value }));
      setErrors((prev) => ({ ...prev, price: null }));
    } 
    // Check if it's a valid whole number (no decimals)
    else if (/^\d+$/.test(value)) {
      setPackageForm((prev) => ({ ...prev, price: value }));
      setErrors((prev) => ({ ...prev, price: null }));
    } 
    else {
      setErrors((prev) => ({ 
        ...prev, 
        price: 'Price must be a whole number (no decimals, commas, or special characters)' 
      }));
    }
  };

  const handleNumberChange = (field, value) => {
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setPackageForm((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: null }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Package name validation
    if (!packageForm.package_name?.trim()) {
      newErrors.package_name = 'Package name is required';
    }
    
    // Price validation
    if (!packageForm.price) {
      newErrors.price = 'Price is required';
    } else if (packageForm.price.toString().includes('.')) {
      newErrors.price = 'Decimal values are not allowed';
    } else if (Number(packageForm.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }
    
    // Duration validation
    if (!packageForm.duration_months) {
      newErrors.duration_months = 'Duration is required';
    } else if (Number(packageForm.duration_months) <= 0) {
      newErrors.duration_months = 'Duration must be greater than 0';
    }
    
    // Seat validation
    const adminSeats = Number(packageForm.max_admins || 0);
    const workerSeats = Number(packageForm.max_workers || 0);
    
    if (adminSeats === 0 && workerSeats === 0) {
      newErrors.seats = 'At least one admin or worker seat must be added';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };

  return (
    <section className="mt-6 overflow-hidden rounded-3xl bg-white shadow-xl">
      <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 text-slate-950">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-cyan-700">{packageForm.id ? 'Edit package' : 'New package'}</div>
            <h3 className="mt-2 text-2xl font-semibold">{packageFormTitle}</h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Only choose how many admins and workers this plan allows. Total seats will be calculated automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Back to packages
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 p-6 lg:grid-cols-[1.4fr_0.8fr]">
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Package name <span className="text-red-500">*</span></span>
              <input 
                className={`w-full rounded-2xl border ${errors.package_name ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`} 
                placeholder="Starter, Growth, Enterprise" 
                value={packageForm.package_name} 
                onChange={(e) => {
                  setPackageForm((prev) => ({ ...prev, package_name: e.target.value }));
                  if (errors.package_name) setErrors((prev) => ({ ...prev, package_name: null }));
                }} 
              />
              {errors.package_name && <p className="mt-1 text-xs text-red-500">{errors.package_name}</p>}
            </label>
            
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Status</span>
              <select 
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100" 
                value={packageForm.status} 
                onChange={(e) => setPackageForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Price <span className="text-red-500">*</span></span>
              <span className="ml-2 text-xs text-slate-500">(Whole numbers only, no decimals)</span>
              <input 
                className={`w-full rounded-2xl border ${errors.price ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`} 
                type="text" 
                inputMode="numeric"
                placeholder="25000" 
                value={packageForm.price} 
                onChange={handlePriceChange}
              />
              {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
            </label>
            
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Duration in months <span className="text-red-500">*</span></span>
              <input 
                className={`w-full rounded-2xl border ${errors.duration_months ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`} 
                type="text" 
                inputMode="numeric"
                placeholder="12" 
                value={packageForm.duration_months} 
                onChange={(e) => handleNumberChange('duration_months', e.target.value)}
              />
              {errors.duration_months && <p className="mt-1 text-xs text-red-500">{errors.duration_months}</p>}
            </label>
            
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Admin seats</span>
              <input 
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100" 
                type="text" 
                inputMode="numeric"
                placeholder="2" 
                value={packageForm.max_admins} 
                onChange={(e) => {
                  handleNumberChange('max_admins', e.target.value);
                  if (errors.seats) setErrors((prev) => ({ ...prev, seats: null }));
                }}
              />
            </label>
            
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Worker seats</span>
              <input 
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100" 
                type="text" 
                inputMode="numeric"
                placeholder="10" 
                value={packageForm.max_workers} 
                onChange={(e) => {
                  handleNumberChange('max_workers', e.target.value);
                  if (errors.seats) setErrors((prev) => ({ ...prev, seats: null }));
                }}
              />
            </label>
          </div>
          
          {errors.seats && <p className="mt-1 text-xs text-red-500">{errors.seats}</p>}
          
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Package features</span>
            <textarea 
              className="min-h-40 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100" 
              placeholder="One feature per line, or comma separated" 
              value={packageForm.features} 
              onChange={(e) => setPackageForm((prev) => ({ ...prev, features: e.target.value }))} 
            />
          </label>
        </div>

        <div className="rounded-3xl bg-slate-50 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-cyan-700">Live summary</div>
          <div className="mt-4 rounded-2xl bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Total seats</div>
            <div className="mt-2 text-4xl font-semibold text-slate-950">{totalSeats}</div>
            <p className="mt-2 text-sm text-slate-500">Admins plus workers. This replaces the old max users field.</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <div className="text-slate-500">Admins</div>
              <div className="mt-1 text-2xl font-semibold">{Number(packageForm.max_admins || 0)}</div>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <div className="text-slate-500">Workers</div>
              <div className="mt-1 text-2xl font-semibold">{Number(packageForm.max_workers || 0)}</div>
            </div>
          </div>
          
          {/* Price Display */}
          {packageForm.price && !errors.price && (
            <div className="mt-4 rounded-2xl bg-gradient-to-r from-cyan-50 to-slate-50 p-4 shadow-sm">
              <div className="text-sm text-slate-500">Price per package</div>
              <div className="mt-1 text-2xl font-semibold text-cyan-700">
                ₹{Number(packageForm.price).toLocaleString('en-IN')}
              </div>
              {packageForm.duration_months && (
                <div className="mt-1 text-xs text-slate-500">
                  for {packageForm.duration_months} month{packageForm.duration_months > 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          Cancel
        </button>
        <button 
          type="button" 
          disabled={isSavingPackage} 
          onClick={handleSave} 
          className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSavingPackage ? 'Saving...' : packageForm.id ? 'Update Package' : 'Create Package'}
        </button>
      </div>
    </section>
  );
};

export default SuperAdminPackageForm;
